import React from "react";
import {Button, ButtonType, Checkbox, EmailInput, Form, Inline, InlineType, Modal, ModalSize, PhoneInput, TextInput} from "@reapptor-apps/reapptor-react-components";
import CruiseWizardPage from "@/pages/Cruise/CruiseWizardPage";
import CruiseSummary from "@/pages/Cruise/Contacts/CruiseSummary/CruiseSummary";
import EstimateCruisePriceRequest from "@/models/server/requests/EstimateCruisePriceRequest";
import {Utility} from "@reapptor-apps/reapptor-toolkit";
import CruiseConstants from "@/helpers/CruiseConstants";
import Localizer from "@/localization/Localizer";

import styles from "./Contacts.module.scss";

export interface IContactsProps {
}

interface IContactsState {
}

export default class Contacts extends CruiseWizardPage<IContactsProps, IContactsState> {
    
    state: IContactsState = {
    };

    private readonly _formRef: React.RefObject<Form> = React.createRef();

    private async setNameAsync(name: string): Promise<void> {
        this.wizard.name = name;
        this.saveContext();
    }
    
    private async setCompanyAsync(company: string): Promise<void> {
        this.wizard.company = company;
        this.saveContext();
    }
    
    private async setEmailAsync(email: string): Promise<void> {
        this.wizard.email = email;
        this.saveContext();
    }
    
    private async setPhoneAsync(phone: string): Promise<void> {
        this.wizard.phone = phone;
        this.saveContext();
    }

    private async setAgreementAcceptedAsync(value: boolean): Promise<void> {
        this.wizard.agreementAccepted = value;
        this.saveContext();

        await this.reRenderAsync();
    }

    private async setRegistrationAcceptedAsync(value: boolean): Promise<void> {
        this.wizard.registrationAccepted = value;
        this.saveContext();

        await this.reRenderAsync();
    }

    private async setCancellationPolicyAcceptedAsync(value: boolean): Promise<void> {
        this.wizard.cancellationPolicyAccepted = value;
        this.saveContext();
        
        await this.reRenderAsync();
    }

    public async nextAsync(): Promise<void> {
        if (this._formRef.current) {
            const isValid: boolean = await this._formRef.current.validateAsync();
            if (isValid) {
                await super.nextAsync();
            }
        }
    }

    public get canNext(): boolean {
        return (
            (!!this.wizard.name) && (!!this.wizard.email) && (!!this.wizard.phone) &&
            (this.wizard.agreementAccepted) && (this.wizard.registrationAccepted) && (this.wizard.cancellationPolicyAccepted)
        );
    }

    public get wide(): boolean {
        return true;
    }
    
    public get leftTitle(): string | null {
        return this.title;
    }
    
    public get rightTitle(): string | null {
        return (this.isFishing)
            ? Localizer.cruiseWizardFishingSummary
            : (this.isHovercraft)
                ? Localizer.cruiseWizardHovercraftSummary
                : Localizer.cruiseWizardSummary;
    }
    
    public get warning(): boolean {
        return (Utility.diff(this.wizard.date, Utility.today()).totalHours < CruiseConstants.cruiseWarningIntervalInHours);
    }
    
    public async initializeAsync(): Promise<void> {
        if ((this.wizard.area) && (this.wizard.area.estimatedPrice)) {

            const request = new EstimateCruisePriceRequest();
            request.areaId = this.wizard.area.id;
            request.extras = this.wizard.extras;
            request.duration = this.wizard.duration!;
            request.groupSize = this.wizard.groupSize!;
            request.groupSizeValue = this.wizard.groupSizeValue;

            const estimatedPrice: number | null = await this.postAsync("api/cruise/estimateCruisePrice", request);

            if (estimatedPrice != this.wizard.estimatedPrice) {
                this.wizard.estimatedPrice = estimatedPrice
                this.saveContext();
                await this.reRenderAsync();
            }
        }
    }

    public renderContent(): React.ReactNode {
        return (
            <Form ref={this._formRef}>
                
                <p>{Localizer.cruiseWizardContactsMessage}</p>
                
                { (this.warning) && <p>{Localizer.cruiseWizardContactsWarning}</p> }
                
                <TextInput name={"name"} required autoFocus trim
                           placeholder={Localizer.cruiseWizardContactName}
                           value={this.wizard.name}
                           onChange={(sender, value) => this.setNameAsync(value)}
                />
                
                <TextInput name={"company"} trim
                           placeholder={Localizer.cruiseWizardCompanyName}
                           value={this.wizard.company}
                           onChange={(sender, value) => this.setCompanyAsync(value)}
                />
                
                <EmailInput name={"email"} required trim
                            placeholder={Localizer.cruiseWizardContactEmail}
                            value={this.wizard.email}
                            onChange={(sender, value) => this.setEmailAsync(value)}
                />
                
                <PhoneInput name={"phone"} required trim
                            placeholder={Localizer.cruiseWizardContactPhone}
                            value={this.wizard.phone}
                            onChange={(sender, value) => this.setPhoneAsync(value)}
                />

                <div className={styles.acceptance}>

                    <Inline className={"m-0"}>
                        <Checkbox inline
                                  id="agreementAccepted"
                                  label={Localizer.myAccountPageAcceptance}
                                  inlineType={InlineType.Right}
                                  value={this.wizard.agreementAccepted}
                                  readonly={this.wizard.agreementAccepted}
                                  onChange={(_, value) => this.setAgreementAcceptedAsync(value)}
                        />

                        <Button className={"ml-n2"}
                                label={Localizer.myAccountPageAcceptanceTerms}
                                type={ButtonType.Text}
                                toggleModal
                                dataTarget="agreementModal"
                        />
                    </Inline>

                    <Inline>
                        <Checkbox inline
                                  id="registrationAccepted"
                                  label={Localizer.myAccountPageAcceptance}
                                  inlineType={InlineType.Right}
                                  value={this.wizard.registrationAccepted}
                                  readonly={this.wizard.registrationAccepted}
                                  onChange={(_, value) => this.setRegistrationAcceptedAsync(value)}
                        />

                        <Button toggleModal
                                className={"ml-n2"}
                                label={Localizer.myAccountPageAcceptancePrivacyNotice}
                                type={ButtonType.Text}
                                dataTarget="privacyNoticeModal"
                        />
                    </Inline>

                    <Inline>
                        <Checkbox inline
                                  id="cancellationPolicyAccepted"
                                  label={Localizer.myAccountPageAcceptance}
                                  inlineType={InlineType.Right}
                                  value={this.wizard.cancellationPolicyAccepted}
                                  readonly={this.wizard.cancellationPolicyAccepted}
                                  onChange={(_, value) => this.setCancellationPolicyAcceptedAsync(value)}
                        />

                        <Button toggleModal
                                className={"ml-n2"}
                                label={Localizer.myAccountPageAcceptanceCancellationPolicy}
                                type={ButtonType.Text}
                                dataTarget="cancellationPolicyModal"
                        />
                    </Inline>

                </div>
                
                <Modal id="agreementModal" info keepTextFormatting
                       className={styles.acceptanceModal}
                       title={Localizer.myAccountPageAcceptanceTermsTitle}
                       content={Localizer.myAccountPageAcceptanceTermsContent}
                       size={ModalSize.Large}
                />

                <Modal id="privacyNoticeModal" info keepTextFormatting
                       className={styles.acceptanceModal}
                       title={Localizer.myAccountPageAcceptancePrivacyNoticeTitle}
                       content={Localizer.myAccountPageAcceptancePrivacyNoticeContent}
                       size={ModalSize.Large}
                />

                <Modal id="cancellationPolicyModal" info keepTextFormatting
                       className={styles.acceptanceModal}
                       title={Localizer.myAccountPageAcceptanceCancellationPolicyTitle}
                       content={Localizer.myAccountPageAcceptanceCancellationPolicyContent}
                       size={ModalSize.Large}
                />
                
            </Form>
        );
    }
    
    public renderRightContent(): React.ReactNode {
        return (
            <CruiseSummary order={this.wizard} />
        );
    }

    public renderButtons(): React.ReactNode {
        return (
            <React.Fragment>
                <Button type={ButtonType.Light} className={"col-6"} label={Localizer.cruiseWizardBack} onClick={() => this.prevAsync()} />

                <Button type={ButtonType.Dark} className={"col-6"} label={Localizer.cruiseWizardSend} onClick={() => this.nextAsync()} />
            </React.Fragment>
        )
    }

    public renderPageContent(): React.ReactNode {
        return (
            <React.Fragment>

                { this.renderLanguage() }

                { this.renderLogo() }
                
                <div className={"row"}>

                    <div className={this.css("col-md-6", styles.leftContent)}>

                        { this.renderContentContainer(this.leftTitle, () => this.renderContent()) }

                        { this.renderButtonsContainer(true) }

                    </div>

                    <div className={this.css("col-md-6", styles.rightContent)}>

                        { this.renderContentContainer(this.rightTitle, () => this.renderRightContent()) }

                    </div>

                </div>

                { this.renderNavigation() }

            </React.Fragment>
        )
    }
}